import api from './api';
import { mutate } from 'swr';

// Helper function to trigger SWR revalidation
export const revalidate = (key: string | string[]) => {
  mutate(key);
};

// Client mutations
export const createClient = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

export const updateClient = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients/${id}`); // Revalidate specific client
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

export const deleteClient = async (id: number) => {
  await api.delete(`/crm-clients/${id}`);
  mutate('/crm-clients'); // Revalidate clients list
};

// Marketing client mutations (uses /crm-clients/marketing endpoint for create)
export const createMarketingClient = async (data: any) => {
  // Marketing endpoint doesn't use FormData, just JSON
  const response = await api.post('/crm-clients/marketing', data);
  mutate('/crm-clients/marketing'); // Revalidate marketing list
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

// Reminder mutations
export const createReminder = async (data: any) => {
  const response = await api.post('/crm-clients-reminders', data);
  mutate('/crm-clients-reminders'); // Revalidate reminders list
  return response.data;
};

export const updateReminder = async (id: number, data: any) => {
  const response = await api.patch(`/crm-clients-reminders/${id}`, data);
  mutate(`/crm-clients-reminders/${id}`); // Revalidate specific reminder
  mutate('/crm-clients-reminders'); // Revalidate reminders list
  return response.data;
};

export const deleteReminder = async (id: number) => {
  await api.delete(`/crm-clients-reminders/${id}`);
  mutate(`/crm-clients-reminders/${id}`); // Revalidate specific reminder
  mutate('/crm-clients-reminders'); // Revalidate reminders list
};

// Task mutations
export const createTask = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-tasks', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const updateTask = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-tasks/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-tasks/${id}`); // Revalidate specific task
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const deleteTask = async (id: number) => {
  await api.delete(`/crm-tasks/${id}`);
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};

// Support Ticket mutations
export const createSupportTicket = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files?.length) {
    files.forEach((file) => formData.append('files', file));
  }
  const response = await api.post('/support-ticket', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/support-ticket');
  mutate('/dashboard/stats');
  return response.data;
};

export const updateSupportTicket = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files?.length) {
    files.forEach((file) => formData.append('files', file));
  }
  const response = await api.patch(`/support-ticket/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/support-ticket/${id}`);
  mutate('/support-ticket');
  mutate('/dashboard/stats');
  return response.data;
};

export const deleteSupportTicket = async (id: number) => {
  await api.delete(`/support-ticket/${id}`);
  mutate('/support-ticket'); // Revalidate tickets list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};

// Invoice mutations
export const createInvoice = async (data: any) => {
  const response = await api.post('/crm-clients-invoices', data);
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const updateInvoice = async (id: number, data: any) => {
  const response = await api.put(`/crm-clients-invoices/${id}`, data);
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const deleteInvoice = async (id: number) => {
  await api.delete(`/crm-clients-invoices/${id}`);
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};
// Todo mutations
export const createTodo = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-todos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-todos'); // Revalidate list
  return response.data;
};

export const updateTodo = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.patch(`/crm-todos/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-todos/${id}`); // Revalidate specific item
  mutate('/crm-todos'); // Revalidate list
  return response.data;
};

export const deleteTodo = async (id: number) => {
  await api.delete(`/crm-todos/${id}`);
  mutate('/crm-todos'); // Revalidate list
};

// Appointment mutations
export const createAppointment = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients-appointments', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients-appointments'); // Revalidate list
  return response.data;
};

export const updateAppointment = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients-appointments/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients-appointments/${id}`); // Revalidate specific item
  mutate('/crm-clients-appointments'); // Revalidate list
  return response.data;
};

export const deleteAppointment = async (id: number) => {
  await api.delete(`/crm-clients-appointments/${id}`);
  mutate('/crm-clients-appointments'); // Revalidate list
};

// Support Agreement mutations
export const createSupportAgreement = async (data: any) => {
  const response = await api.post('/crm-support-agreements', data);
  mutate('/crm-support-agreements'); // Revalidate list
  return response.data;
};

export const updateSupportAgreement = async (id: number, data: any) => {
  const response = await api.patch(`/crm-support-agreements/${id}`, data);
  mutate(`/crm-support-agreements/${id}`); // Revalidate specific item
  mutate('/crm-support-agreements'); // Revalidate list
  return response.data;
};

export const deleteSupportAgreement = async (id: number) => {
  await api.delete(`/crm-support-agreements/${id}`);
  mutate('/crm-support-agreements'); // Revalidate list
};

// Call mutations
export const createCall = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-calls', data);
  mutate('/crm-clients-calls'); // Revalidate calls list
  return response.data;
};

export const updateCall = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-calls/${id}`, data);
  mutate(`/crm-clients-calls/${id}`); // Revalidate specific call
  mutate('/crm-clients-calls'); // Revalidate calls list
  return response.data;
};

export const deleteCall = async (id: number) => {
  await api.delete(`/crm-clients-calls/${id}`);
  mutate(`/crm-clients-calls/${id}`); // Revalidate specific call
  mutate('/crm-clients-calls'); // Revalidate calls list
};

// Lead mutations
export const createLead = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-leads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-leads'); // Revalidate leads list
  return response.data;
};

export const updateLead = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-leads/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-leads/${id}`); // Revalidate specific lead
  mutate('/crm-leads'); // Revalidate leads list
  return response.data;
};

export const deleteLead = async (id: number) => {
  await api.delete(`/crm-leads/${id}`);
  mutate(`/crm-leads/${id}`); // Revalidate specific lead
  mutate('/crm-leads'); // Revalidate leads list
};

// Notes mutations
export const createCrmNote = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-notes', data);
  mutate('/crm-notes'); // Revalidate notes list
  return response.data;
};

export const updateCrmNote = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-notes/${id}`, data);
  mutate(`/crm-notes/${id}`); // Revalidate specific note
  mutate('/crm-notes'); // Revalidate notes list
  return response.data;
};

export const deleteCrmNote = async (id: number) => {
  await api.delete(`/crm-notes/${id}`);
  mutate(`/crm-notes/${id}`); // Revalidate specific note
  mutate('/crm-notes'); // Revalidate notes list
};

// Client Services mutations
export const createClientService = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-services', data);
  mutate('/crm-clients-services'); // Revalidate services list
  return response.data;
};

export const updateClientService = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-services/${id}`, data);
  mutate(`/crm-clients-services/${id}`); // Revalidate specific service
  mutate('/crm-clients-services'); // Revalidate services list
  return response.data;
};

export const deleteClientService = async (id: number) => {
  await api.delete(`/crm-clients-services/${id}`);
  mutate(`/crm-clients-services/${id}`); // Revalidate specific service
  mutate('/crm-clients-services'); // Revalidate services list
};

// Invoice Payment mutations
export const createInvoicePayment = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-invoice-payments', data);
  mutate('/crm-clients-invoice-payments'); // Revalidate payments list
  mutate('/crm-clients-invoices'); // Revalidate invoices list (payment status)
  return response.data;
};

export const updateInvoicePayment = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-invoice-payments/${id}`, data);
  mutate(`/crm-clients-invoice-payments/${id}`); // Revalidate specific item
  mutate('/crm-clients-invoice-payments'); // Revalidate list
  return response.data;
};

export const deleteInvoicePayment = async (id: number) => {
  await api.delete(`/crm-clients-invoice-payments/${id}`);
  mutate(`/crm-clients-invoice-payments/${id}`); // Revalidate specific item
  mutate('/crm-clients-invoice-payments'); // Revalidate list
};

// Client Invoice mutations
export const createClientInvoice = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients-invoices', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  return response.data;
};

export const updateClientInvoice = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients-invoices/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  return response.data;
};

export const deleteClientInvoice = async (id: number) => {
  await api.delete(`/crm-clients-invoices/${id}`);
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
};

// Supplier mutations
export const createSupplier = async (data: any) => {
  const response = await api.post('/suppliers', data);
  mutate('/suppliers');
  return response.data;
};

export const updateSupplier = async (id: number, data: any) => {
  const response = await api.patch(`/suppliers/${id}`, data);
  mutate(`/suppliers/${id}`);
  mutate('/suppliers');
  return response.data;
};

export const deleteSupplier = async (id: number) => {
  await api.delete(`/suppliers/${id}`);
  mutate('/suppliers');
};

// Coerce supplier invoice payload: id fields as numbers, empty strings -> null for optional FKs
const coerceSupplierInvoicePayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.supplierId !== undefined && out.supplierId !== '' && out.supplierId !== null) {
    out.supplierId = Number(out.supplierId);
  }
  if (out.invoiceTypeId !== undefined && (out.invoiceTypeId === '' || out.invoiceTypeId === null)) {
    out.invoiceTypeId = null;
  } else if (out.invoiceTypeId !== undefined && out.invoiceTypeId !== '') {
    out.invoiceTypeId = Number(out.invoiceTypeId);
  }
  if (out.invoiceCategoryId !== undefined && (out.invoiceCategoryId === '' || out.invoiceCategoryId === null)) {
    out.invoiceCategoryId = null;
  } else if (out.invoiceCategoryId !== undefined && out.invoiceCategoryId !== '') {
    out.invoiceCategoryId = Number(out.invoiceCategoryId);
  }
  return out;
};

// Supplier Invoice mutations
export const createSupplierInvoice = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(coerceSupplierInvoicePayload(data)));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/supplier-invoices', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/supplier-invoices');
  return response.data;
};

export const updateSupplierInvoice = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(coerceSupplierInvoicePayload(data)));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/supplier-invoices/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/supplier-invoices/${id}`);
  mutate('/supplier-invoices');
  return response.data;
};

export const deleteSupplierInvoice = async (id: number) => {
  await api.delete(`/supplier-invoices/${id}`);
  mutate(`/supplier-invoices/${id}`);
  mutate('/supplier-invoices');
};

// Supplier Invoice Payment mutations
export const createSupplierInvoicePayment = async (data: any) => {
  const response = await api.post('/supplier-invoice-payments', data);
  mutate('/supplier-invoice-payments');
  mutate('/supplier-invoices'); // Revalidate invoices list (payment status)
  return response.data;
};

export const updateSupplierInvoicePayment = async (id: number, data: any) => {
  const response = await api.patch(`/supplier-invoice-payments/${id}`, data);
  mutate(`/supplier-invoice-payments/${id}`);
  mutate('/supplier-invoice-payments');
  return response.data;
};

export const deleteSupplierInvoicePayment = async (id: number) => {
  await api.delete(`/supplier-invoice-payments/${id}`);
  mutate(`/supplier-invoice-payments/${id}`);
  mutate('/supplier-invoice-payments');
};

// Expense mutations
export const createExpense = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-expense', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-expense'); // Revalidate expenses list
  return response.data;
};

export const updateExpense = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-expense/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-expense/${id}`); // Revalidate specific expense
  mutate('/crm-expense'); // Revalidate expenses list
  return response.data;
};

export const deleteExpense = async (id: number) => {
  await api.delete(`/crm-expense/${id}`);
  mutate(`/crm-expense/${id}`); // Revalidate specific expense
  mutate('/crm-expense'); // Revalidate expenses list
};

// Payables mutations
export const createPayable = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/payables', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/payables');
  return response.data;
};

export const updatePayable = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/payables/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/payables/${id}`);
  mutate('/payables');
  return response.data;
};

export const deletePayable = async (id: number) => {
  await api.delete(`/payables/${id}`);
  mutate(`/payables/${id}`);
  mutate('/payables');
};

export interface ExtractedPayableInvoice {
  payeeName: string | null;
  payeeId: number | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  amount: number | null;
  currency: string | null;
  notes: string | null;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  possibleDuplicates: Array<{
    id: number;
    payeeName: string;
    amount: number;
    dueDate: string | null;
    status: string;
  }>;
  extractionMethod: 'ai' | 'heuristic';
  modelName: string | null;
}

/** Upload an invoice PDF/image and extract fields for a new payable draft. */
export const extractPayableInvoice = async (file: File): Promise<ExtractedPayableInvoice> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/payables/extract-invoice', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

// Offers mutations
export const createOffer = async (data: Record<string, unknown>, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/offers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/offers');
  return response.data;
};

export const updateOffer = async (id: number, data: Record<string, unknown>, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/offers/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/offers/${id}`);
  mutate('/offers');
  return response.data;
};

export const deleteOffer = async (id: number) => {
  await api.delete(`/offers/${id}`);
  mutate(`/offers/${id}`);
  mutate('/offers');
};

export const duplicateOffer = async (id: number): Promise<{ id: number }> => {
  const response = await api.post(`/offers/${id}/duplicate`);
  mutate('/offers');
  return response.data;
};

// Catalog items mutations
export const createCatalogItem = async (data: { type: string; name: string; description?: string; defaultPrice?: number; manufacturerId?: number; categoryId?: number; isActive?: boolean; sortOrder?: number }) => {
  const response = await api.post('/catalog-items', data);
  mutate('/catalog-items');
  return response.data;
};

export const updateCatalogItem = async (id: number, data: Partial<{ type: string; name: string; description?: string; defaultPrice?: number; manufacturerId?: number | null; categoryId?: number | null; isActive?: boolean; sortOrder?: number; primaryFileAttachmentId?: number | null }>) => {
  const response = await api.patch(`/catalog-items/${id}`, data);
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
  return response.data;
};

export const deleteCatalogItem = async (id: number) => {
  await api.delete(`/catalog-items/${id}`);
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
};

export const duplicateCatalogItem = async (id: number) => {
  const response = await api.post(`/catalog-items/${id}/duplicate`);
  mutate('/catalog-items');
  mutate(`/catalog-items/${response.data.id}`);
  return response.data;
};

export const uploadCatalogItemFiles = async (id: number, files: File[]) => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  const response = await api.post(`/catalog-items/${id}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
  return response.data;
};

export const deleteCatalogItemFile = async (itemId: number, fileId: number) => {
  const response = await api.delete(`/catalog-items/${itemId}/files/${fileId}`);
  mutate(`/catalog-items/${itemId}`);
  mutate('/catalog-items');
  return response.data;
};

// Payees mutations (for Payable / RecurringPayable select)
export const createPayee = async (data: {
  name: string;
  notes?: string;
  email?: string;
  phone?: string;
  address?: string;
  vatNumber?: string;
  isActive?: boolean;
  defaultCategoryId?: number | null;
  defaultSubcategoryId?: number | null;
}) => {
  const response = await api.post('/payees', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
  return response.data;
};

export const updatePayee = async (
  id: number,
  data: {
    name?: string;
    notes?: string;
    email?: string;
    phone?: string;
    address?: string;
    vatNumber?: string;
    isActive?: boolean;
    defaultCategoryId?: number | null;
    defaultSubcategoryId?: number | null;
  }
) => {
  const response = await api.patch(`/payees/${id}`, data);
  mutate(`/payees/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
  return response.data;
};

export const deletePayee = async (id: number) => {
  await api.delete(`/payees/${id}`);
  mutate(`/payees/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
};

export const createOfferTemplate = async (data: { name: string; isDefault?: boolean; introText?: string; deliveryText?: string; paymentMethodText?: string; notes?: string }) => {
  const response = await api.post('/offer-templates', data);
  mutate('/offer-templates');
  return response.data;
};

export const updateOfferTemplate = async (id: number, data: Partial<{ name: string; isDefault?: boolean; introText?: string; deliveryText?: string; paymentMethodText?: string; notes?: string }>) => {
  const response = await api.patch(`/offer-templates/${id}`, data);
  mutate(`/offer-templates/${id}`);
  mutate('/offer-templates');
  return response.data;
};

export const deleteOfferTemplate = async (id: number) => {
  await api.delete(`/offer-templates/${id}`);
  mutate(`/offer-templates/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/offer-templates'), undefined, { revalidate: true });
};

export const markPayablePaid = async (id: number, paymentDate: string, paymentAmount?: number) => {
  const response = await api.patch(`/payables/${id}/mark-paid`, { paymentDate, paymentAmount });
  mutate(`/payables/${id}`);
  mutate('/payables');
  return response.data;
};

export const createRecurringPayable = async (data: any) => {
  const response = await api.post('/recurring-payables', data);
  mutate('/recurring-payables');
  return response.data;
};

export const updateRecurringPayable = async (id: number, data: any) => {
  const response = await api.patch(`/recurring-payables/${id}`, data);
  mutate(`/recurring-payables/${id}`);
  mutate('/recurring-payables');
  return response.data;
};

export const deleteRecurringPayable = async (id: number) => {
  await api.delete(`/recurring-payables/${id}`);
  mutate(`/recurring-payables/${id}`);
  mutate('/recurring-payables');
};

/** Permanently delete a file attachment record and the file from disk (for Deleted Files list). */
export const permanentDeleteFileAttachment = async (id: number) => {
  await api.delete(`/file-attachment/${id}/permanent`);
  mutate('/file-attachment/deleted');
};

/** Delete all FileAttachment records that have no file on disk (orphaned) or no filePath. */
export const deleteOrphanedFileAttachments = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/orphaned');
  mutate('/file-attachment/deleted');
  return res.data;
};

/** Delete from disk all files that have no FileAttachment record. */
export const deleteFilesWithoutRecord = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/files-without-record');
  return res.data;
};

// Parameter module mutations - Support Settings
export const createTaskType = async (data: any) => {
  const response = await api.post('/p-crm-task-type', data);
  mutate('/p-crm-task-type');
  return response.data;
};

export const updateTaskType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-type/${id}`, data);
  mutate(`/p-crm-task-type/${id}`);
  mutate('/p-crm-task-type');
  return response.data;
};

export const deleteTaskType = async (id: number) => {
  await api.delete(`/p-crm-task-type/${id}`);
  mutate('/p-crm-task-type');
};

export const createTaskCategory = async (data: any) => {
  const response = await api.post('/p-crm-task-category', data);
  mutate('/p-crm-task-category');
  return response.data;
};

export const updateTaskCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-category/${id}`, data);
  mutate(`/p-crm-task-category/${id}`);
  mutate('/p-crm-task-category');
  return response.data;
};

export const deleteTaskCategory = async (id: number) => {
  await api.delete(`/p-crm-task-category/${id}`);
  mutate('/p-crm-task-category');
};

export const createTaskStatus = async (data: any) => {
  const response = await api.post('/p-crm-task-status', data);
  mutate('/p-crm-task-status');
  return response.data;
};

export const updateTaskStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-status/${id}`, data);
  mutate(`/p-crm-task-status/${id}`);
  mutate('/p-crm-task-status');
  return response.data;
};

export const deleteTaskStatus = async (id: number) => {
  await api.delete(`/p-crm-task-status/${id}`);
  mutate('/p-crm-task-status');
};

export const createPriority = async (data: any) => {
  const response = await api.post('/p-crm-priority', data);
  mutate('/p-crm-priority');
  return response.data;
};

export const updatePriority = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-priority/${id}`, data);
  mutate(`/p-crm-priority/${id}`);
  mutate('/p-crm-priority');
  return response.data;
};

export const deletePriority = async (id: number) => {
  await api.delete(`/p-crm-priority/${id}`);
  mutate('/p-crm-priority');
};

export const createTicketStatus = async (data: any) => {
  const response = await api.post('/p-crm-ticket-status', data);
  mutate('/p-crm-ticket-status');
  return response.data;
};

export const updateTicketStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-ticket-status/${id}`, data);
  mutate(`/p-crm-ticket-status/${id}`);
  mutate('/p-crm-ticket-status');
  return response.data;
};

export const deleteTicketStatus = async (id: number) => {
  await api.delete(`/p-crm-ticket-status/${id}`);
  mutate('/p-crm-ticket-status');
};

export const createCallType = async (data: any) => {
  const response = await api.post('/call-type', data);
  mutate('/call-type');
  return response.data;
};

export const updateCallType = async (id: number, data: any) => {
  const response = await api.patch(`/call-type/${id}`, data);
  mutate(`/call-type/${id}`);
  mutate('/call-type');
  return response.data;
};

export const deleteCallType = async (id: number) => {
  await api.delete(`/call-type/${id}`);
  mutate('/call-type');
};

// Parameter module mutations - Service Settings
export const createServiceType = async (data: any) => {
  const response = await api.post('/p-crm-service-type', data);
  mutate('/p-crm-service-type');
  return response.data;
};

export const updateServiceType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-service-type/${id}`, data);
  mutate(`/p-crm-service-type/${id}`);
  mutate('/p-crm-service-type');
  return response.data;
};

export const deleteServiceType = async (id: number) => {
  await api.delete(`/p-crm-service-type/${id}`);
  mutate('/p-crm-service-type');
};

export const createFrequencyType = async (data: any) => {
  const response = await api.post('/p-frequency-type', data);
  mutate('/p-frequency-type');
  return response.data;
};

export const updateFrequencyType = async (id: number, data: any) => {
  const response = await api.patch(`/p-frequency-type/${id}`, data);
  mutate(`/p-frequency-type/${id}`);
  mutate('/p-frequency-type');
  return response.data;
};

export const deleteFrequencyType = async (id: number) => {
  await api.delete(`/p-frequency-type/${id}`);
  mutate('/p-frequency-type');
};

export const createDuration = async (data: any) => {
  const response = await api.post('/p-crm-duration', data);
  mutate('/p-crm-duration');
  return response.data;
};

export const updateDuration = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-duration/${id}`, data);
  mutate(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration');
  return response.data;
};

export const deleteDuration = async (id: number) => {
  await api.delete(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration');
};

export const createServiceCategory = async (data: any) => {
  const response = await api.post('/p-crm-service-category', data);
  mutate('/p-crm-service-category');
  return response.data;
};

export const updateServiceCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-service-category/${id}`, data);
  mutate(`/p-crm-service-category/${id}`);
  mutate('/p-crm-service-category');
  return response.data;
};

export const deleteServiceCategory = async (id: number) => {
  await api.delete(`/p-crm-service-category/${id}`);
  mutate('/p-crm-service-category');
};

// Parameter module mutations - Reminder Settings
export const createReminderType = async (data: any) => {
  const response = await api.post('/p-crm-reminder-type', data);
  mutate('/p-crm-reminder-type');
  return response.data;
};

export const updateReminderType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-reminder-type/${id}`, data);
  mutate(`/p-crm-reminder-type/${id}`);
  mutate('/p-crm-reminder-type');
  return response.data;
};

export const deleteReminderType = async (id: number) => {
  await api.delete(`/p-crm-reminder-type/${id}`);
  mutate('/p-crm-reminder-type');
};

// Parameter module mutations - Business Settings
export const createTaxAuthority = async (data: any) => {
  const response = await api.post('/p-tax-authority', data);
  mutate('/p-tax-authority');
  return response.data;
};

export const updateTaxAuthority = async (id: number, data: any) => {
  const response = await api.patch(`/p-tax-authority/${id}`, data);
  mutate(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority');
  return response.data;
};

export const deleteTaxAuthority = async (id: number) => {
  await api.delete(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority');
};

export const createCountry = async (data: any) => {
  const response = await api.post('/p-country', data);
  mutate('/p-country');
  return response.data;
};

export const updateCountry = async (id: number, data: any) => {
  const response = await api.patch(`/p-country/${id}`, data);
  mutate(`/p-country/${id}`);
  mutate('/p-country');
  return response.data;
};

export const deleteCountry = async (id: number) => {
  await api.delete(`/p-country/${id}`);
  mutate('/p-country');
};

export const createAppStatus = async (data: any) => {
  const response = await api.post('/p-crm-app-status', data);
  mutate('/p-crm-app-status');
  return response.data;
};

export const updateAppStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-app-status/${id}`, data);
  mutate(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status');
  return response.data;
};

export const deleteAppStatus = async (id: number) => {
  await api.delete(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status');
};

// Parameter module mutations - Finance Settings
export const createInvoiceCategory = async (data: any) => {
  const response = await api.post('/p-crm-invoice-category', data);
  mutate('/p-crm-invoice-category');
  return response.data;
};

export const updateInvoiceCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-invoice-category/${id}`, data);
  mutate(`/p-crm-invoice-category/${id}`);
  mutate('/p-crm-invoice-category');
  return response.data;
};

export const deleteInvoiceCategory = async (id: number) => {
  await api.delete(`/p-crm-invoice-category/${id}`);
  mutate('/p-crm-invoice-category');
};

export const createInvoiceType = async (data: any) => {
  const response = await api.post('/p-crm-invoice-type', data);
  mutate('/p-crm-invoice-type');
  return response.data;
};

export const updateInvoiceType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-invoice-type/${id}`, data);
  mutate(`/p-crm-invoice-type/${id}`);
  mutate('/p-crm-invoice-type');
  return response.data;
};

export const deleteInvoiceType = async (id: number) => {
  await api.delete(`/p-crm-invoice-type/${id}`);
  mutate('/p-crm-invoice-type');
};

// Parameter module mutations - Other
export const createTag = async (data: any) => {
  const response = await api.post('/tags', data);
  mutate('/tags');
  return response.data;
};

export const updateTag = async (id: number, data: any) => {
  const response = await api.patch(`/tags/${id}`, data);
  mutate(`/tags/${id}`);
  mutate('/tags');
  return response.data;
};

export const deleteTag = async (id: number) => {
  await api.delete(`/tags/${id}`);
  mutate('/tags');
};

// Expense Category mutations
export const createExpenseCategory = async (data: any) => {
  const response = await api.post('/crm-expense-category', data);
  mutate('/crm-expense-category');
  return response.data;
};

export const updateExpenseCategory = async (id: number, data: any) => {
  const response = await api.patch(`/crm-expense-category/${id}`, data);
  mutate(`/crm-expense-category/${id}`);
  mutate('/crm-expense-category');
  return response.data;
};

export const deleteExpenseCategory = async (id: number) => {
  await api.delete(`/crm-expense-category/${id}`);
  mutate('/crm-expense-category');
};

// Expense Sub Category mutations
export const createExpenseSubCategory = async (data: any) => {
  const response = await api.post('/crm-expense-sub-category', data);
  mutate('/crm-expense-sub-category');
  return response.data;
};

export const updateExpenseSubCategory = async (id: number, data: any) => {
  const response = await api.patch(`/crm-expense-sub-category/${id}`, data);
  mutate(`/crm-expense-sub-category/${id}`);
  mutate('/crm-expense-sub-category');
  return response.data;
};

export const deleteExpenseSubCategory = async (id: number) => {
  await api.delete(`/crm-expense-sub-category/${id}`);
  mutate('/crm-expense-sub-category');
};

// Project mutations
export const createProject = async (data: any) => {
  const response = await api.post('/crm-projects', data);
  mutate('/crm-projects');
  return response.data;
};

export const updateProject = async (id: number, data: any) => {
  const response = await api.patch(`/crm-projects/${id}`, data);
  mutate(`/crm-projects/${id}`);
  mutate('/crm-projects');
  return response.data;
};

export const deleteProject = async (id: number) => {
  await api.delete(`/crm-projects/${id}`);
  mutate('/crm-projects');
};

// Package mutations
export const createPackage = async (data: any) => {
  const response = await api.post('/crm-packages', data);
  mutate('/crm-packages');
  return response.data;
};

export const updatePackage = async (id: number, data: any) => {
  const response = await api.patch(`/crm-packages/${id}`, data);
  mutate(`/crm-packages/${id}`);
  mutate('/crm-packages');
  return response.data;
};

export const deletePackage = async (id: number) => {
  await api.delete(`/crm-packages/${id}`);
  mutate('/crm-packages');
};

// User mutations
export const createUser = async (data: any) => {
  const response = await api.post('/users', data);
  mutate('/users');
  return response.data;
};

export const updateUser = async (id: number, data: any) => {
  // Remove password if it's empty (don't update password if not provided)
  const updateData = { ...data };
  if (updateData.userPassword === '' || updateData.userPassword === undefined) {
    delete updateData.userPassword;
  }
  
  const response = await api.patch(`/users/${id}`, updateData);
  mutate(`/users/${id}`);
  mutate('/users');
  return response.data;
};

export const deleteUser = async (id: number) => {
  await api.delete(`/users/${id}`);
  mutate('/users');
};

export const sendUserInvitation = async (data: { email: string; role?: string }) => {
  const response = await api.post('/users/invitations', data);
  mutate('/users');
  return response.data;
};

// Manufacturer mutations
export const createManufacturer = async (data: Partial<{ name: string }>) => {
  const response = await api.post('/manufacturers', data);
  mutate('/manufacturers');
  return response.data;
};

export const updateManufacturer = async (id: number, data: Partial<{ name: string }>) => {
  const response = await api.patch(`/manufacturers/${id}`, data);
  mutate(`/manufacturers/${id}`);
  mutate('/manufacturers');
  return response.data;
};

export const deleteManufacturer = async (id: number) => {
  await api.delete(`/manufacturers/${id}`);
  mutate('/manufacturers');
};

// Catalog Category mutations
export const createCatalogCategory = async (data: Partial<{ name: string }>) => {
  const response = await api.post('/catalog-categories', data);
  mutate('/catalog-categories');
  return response.data;
};

export const updateCatalogCategory = async (id: number, data: Partial<{ name: string }>) => {
  const response = await api.patch(`/catalog-categories/${id}`, data);
  mutate(`/catalog-categories/${id}`);
  mutate('/catalog-categories');
  return response.data;
};

export const deleteCatalogCategory = async (id: number) => {
  await api.delete(`/catalog-categories/${id}`);
  mutate('/catalog-categories');
};